const NAMED = {
  black: [0, 0, 0], white: [255, 255, 255], grey: [128, 128, 128], gray: [128, 128, 128],
  silver: [192, 192, 192], lightgray: [211, 211, 211], lightgrey: [211, 211, 211],
  red: [255, 0, 0], green: [0, 128, 0], blue: [0, 0, 255], yellow: [255, 255, 0],
  orange: [255, 165, 0], purple: [128, 0, 128], darkgray: [169, 169, 169], darkgrey: [169, 169, 169],
  antiquewhite: [250, 235, 215],
}

function parseColor(value) {
  const color = value.trim().toLowerCase()
  if (NAMED[color]) return [...NAMED[color], 1]
  if (color[0] === '#') {
    let hex = color.slice(1)
    if (hex.length === 3 || hex.length === 4) hex = hex.split('').map(char => char + char).join('')
    if (hex.length !== 6 && hex.length !== 8) return null
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16), hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1]
  }
  if (/^rgba?\(/.test(color)) {
    const parts = color.slice(color.indexOf('(') + 1, -1).split(/[,/\s]+/).filter(Boolean)
    if (parts.length < 3 || parts.some(part => !/^\d*\.?\d+%?$/.test(part))) return null
    return parts.slice(0, 3).map(part => part.endsWith('%') ? parseFloat(part) * 2.55 : Number(part)).concat(parts[3] ? parseFloat(parts[3]) / (parts[3].endsWith('%') ? 100 : 1) : 1)
  }
  return null
}

function tone(red, green, blue) {
  const r = red / 255, g = green / 255, b = blue / 255
  const high = Math.max(r, g, b), low = Math.min(r, g, b), difference = high - low
  const lightness = (high + low) / 2
  const saturation = difference === 0 ? 0 : difference / (1 - Math.abs(2 * lightness - 1))
  let hue = difference === 0 ? 0 : high === r ? ((g - b) / difference) % 6 : high === g ? (b - r) / difference + 2 : (r - g) / difference + 4
  hue = (hue * 60 + 360) % 360
  const neutral = difference * 255 < (lightness > .8 ? 12 : lightness < .4 ? 55 : 40) || saturation < .22
  const family = hue < 20 || hue >= 330 ? 'danger' : hue < 35 ? 'orange' : hue < 70 ? 'warning' : hue < 175 ? 'success' : hue < 235 ? 'info' : 'primary'
  return { neutral, lightness, family }
}

// Returns semantic overrides with a fallback, so light mode and printed output
// retain the original colour. It also supports colours supplied by UI data.
export function themeColor(value, property = 'color') {
  if (typeof value !== 'string' || value.includes('var(')) return value
  const parsed = parseColor(value)
  if (!parsed) return value
  const [r, g, b, alpha] = parsed
  if (alpha === 0) return value
  const { neutral, lightness, family } = tone(r, g, b)
  const role = property.toLowerCase()
  const background = /background|^bg$/.test(role)
  const border = /border|outline|stroke/.test(role)
  let token
  if (neutral) {
    if (background) {
      // Transparent black is an overlay or a shadow; preserve its opacity.
      if (alpha < 1 && lightness < .35) return value
      token = lightness > .985 ? 'surface' : lightness > .90 ? 'surface-muted' : lightness > .6 ? 'surface-hover' : lightness < .35 ? 'surface-contrast' : 'surface-muted'
    } else if (border) token = lightness < .5 ? 'border-strong' : 'border'
    else {
      // White labels on solid action buttons retain their intended contrast.
      if (r >= 250 && g >= 250 && b >= 250) return value
      token = lightness < .3 || lightness > .8 ? 'text' : lightness > .6 ? 'text-subtle' : 'text-muted'
    }
  } else if (background) token = `${family}${lightness > .80 ? '-soft' : ''}`
  else if (border) token = `${family}-border`
  else token = `${family}-text`
  const variable = `var(--theme-${token}, ${value})`
  if (alpha < 1) {
    const opaque = `rgb(${r}, ${g}, ${b})`
    return `color-mix(in srgb, var(--theme-${token}, ${opaque}) ${Math.round(alpha * 1000) / 10}%, transparent)`
  }
  return variable
}
